import React, { useMemo, useState } from "react";

const SEPARATORI_RAPIDI = [
  { etichetta: "Spazio", valore: " " },
  { etichetta: "Virgola", valore: "," },
  { etichetta: "Trattino", valore: "-" },
  { etichetta: "Slash", valore: "/" },
  // Celle PDF con più righe impilate (codice / colore / taglia): vedi unisciPezziCella in routes/pdfEstrattore.js.
  { etichetta: "A capo", valore: "\n" },
];

function dividi(testo, separatore) {
  if (!separatore) return [testo];
  // Un a capo nel valore (celle PDF su più righe) separa i pezzi solo con il separatore "A capo".
  if (separatore !== "\n") testo = testo.replace(/\n/g, " ");
  return testo.split(separatore).map((p) => p.trim()).filter((p) => p !== "");
}

const stileBtnAttivo = { backgroundColor: "#1e3a8a", color: "#fff", borderColor: "#1e3a8a" };

// Stesso principio visuale di EstrazioneModal.jsx (chip cliccabili su un
// valore VERO, non numeri di posizione astratti), applicato a un caso
// diverso: qui non si sceglie UNA pillola, si clicca DOVE TAGLIARE il
// valore in due — tutto quello fino alla pillola cliccata (compresa) va
// nella prima colonna, il resto nella seconda.
export default function DivisioneModal({
  nomeColonna, esempi, divisioneIniziale, onConferma, onAnnulla,
}) {
  const [indiceEsempio, setIndiceEsempio] = useState(0);
  const [separatore, setSeparatore] = useState(divisioneIniziale?.separatore ?? " ");
  const [separatorePersonalizzato, setSeparatorePersonalizzato] = useState(
    SEPARATORI_RAPIDI.some((s) => s.valore === (divisioneIniziale?.separatore ?? " ")) ? "" : (divisioneIniziale?.separatore ?? "")
  );
  const [indiceTaglio, setIndiceTaglio] = useState(divisioneIniziale?.indiceTaglio ?? null);
  const [nomeParte1, setNomeParte1] = useState(divisioneIniziale?.nomeParte1 ?? nomeColonna);
  const [nomeParte2, setNomeParte2] = useState(divisioneIniziale?.nomeParte2 ?? "");
  // Parte opzionale in fondo (es. una taglia "T34" che a volte c'è e a volte
  // no): la 2ª colonna si ferma a "indiceFine", quel che resta va in una 3ª
  // colonna oppure, senza nome, si scarta.
  const [indiceFine, setIndiceFine] = useState(divisioneIniziale?.indiceFine ?? null);
  const [terzaAttiva, setTerzaAttiva] = useState(divisioneIniziale?.indiceFine != null);
  const [nomeParte3, setNomeParte3] = useState(divisioneIniziale?.nomeParte3 ?? "");

  const valoreEsempio = esempi[indiceEsempio] ?? "";
  const parti = useMemo(() => dividi(valoreEsempio, separatore), [valoreEsempio, separatore]);

  // Il punto di taglio è un conteggio di pillole dall'inizio, quindi con un
  // separatore (o una riga di esempio) diversi cambia il numero di pillole e
  // il vecchio valore può non avere più senso. Azzerarlo però lasciava
  // "Dividi" disabilitato finché non si ricliccava una pillola: si tiene il
  // taglio precedente se è ancora valido, altrimenti si propone il caso
  // tipico "prima pillola | tutto il resto" (serve almeno 2 pillole).
  function tagliaProposto(valore, sep, precedente) {
    const n = dividi(valore, sep).length;
    if (precedente != null && precedente >= 1 && precedente < n) return precedente;
    return n >= 2 ? 1 : null;
  }

  // Il secondo taglio (fine della 2ª colonna) segue la stessa regola: si tiene
  // se è ancora dentro al testo e dopo il primo taglio, altrimenti sparisce.
  function aggiornaTagli(valore, sep) {
    const t = tagliaProposto(valore, sep, indiceTaglio);
    setIndiceTaglio(t);
    setIndiceFine((f) => (f != null && t != null && f > t && f < dividi(valore, sep).length ? f : null));
  }

  function scegliSeparatoreRapido(v) {
    setSeparatore(v);
    setSeparatorePersonalizzato("");
    aggiornaTagli(valoreEsempio, v);
  }
  function scegliSeparatorePersonalizzato(v) {
    setSeparatorePersonalizzato(v);
    setSeparatore(v);
    aggiornaTagli(valoreEsempio, v);
  }
  function cambiaEsempio(nuovoIndice) {
    setIndiceEsempio(nuovoIndice);
    aggiornaTagli(esempi[nuovoIndice] ?? "", separatore);
  }

  // Click su una pillola. Senza terza parte: è sempre il taglio fra 1ª e 2ª
  // colonna. Con la terza parte attiva, una pillola già nella 2ª zona (dopo il
  // primo taglio) fissa invece dove finisce la 2ª colonna.
  function cliccaPillola(p) {
    if (terzaAttiva && indiceTaglio != null && p >= indiceTaglio) {
      setIndiceFine(p + 1 < parti.length ? p + 1 : null);
      return;
    }
    setIndiceTaglio(p + 1);
    setIndiceFine((f) => (f != null && f > p + 1 ? f : null));
  }

  function cambiaTerzaAttiva(attiva) {
    setTerzaAttiva(attiva);
    if (!attiva) setIndiceFine(null);
  }

  const unisci = (pezzi) => pezzi.join(separatore === " " ? " " : ` ${separatore} `);
  const fineEffettiva = indiceFine ?? parti.length;
  const parte1Anteprima = indiceTaglio != null ? unisci(parti.slice(0, indiceTaglio)) : null;
  const parte2Anteprima = indiceTaglio != null ? unisci(parti.slice(indiceTaglio, fineEffettiva)) : null;
  const parte3Anteprima = indiceTaglio != null && terzaAttiva ? unisci(parti.slice(fineEffettiva)) : null;

  const puoConfermare = indiceTaglio != null && nomeParte1.trim() !== "" && nomeParte2.trim() !== "";

  function confermaScelta() {
    if (!puoConfermare) return;
    const terza = terzaAttiva && indiceFine != null
      ? { indiceFine, ...(nomeParte3.trim() ? { nomeParte3: nomeParte3.trim() } : {}) }
      : {};
    onConferma({ separatore, indiceTaglio, nomeParte1: nomeParte1.trim(), nomeParte2: nomeParte2.trim(), ...terza });
  }

  return (
    <div
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, fontFamily: "Inter, sans-serif" }}
      onClick={onAnnulla}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: "var(--cf-bg-card)", borderRadius: "16px", width: "560px", maxWidth: "92vw", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.45)" }}
      >
        <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "20px", lineHeight: 1 }}>✂️</span>
            <div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontWeight: 700, fontSize: "15px", letterSpacing: "1px", textTransform: "uppercase", color: "#f1f5f9" }}>
                Dividi in due colonne
              </div>
              <div style={{ fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "11px", color: "var(--cf-text-faint)", letterSpacing: "0.3px", marginTop: "3px" }}>
                {nomeColonna}
              </div>
            </div>
          </div>
          <button className="cf-btn" onClick={onAnnulla} style={{ border: "none", background: "rgba(255,255,255,0.08)", borderRadius: "6px", width: "28px", height: "28px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", cursor: "pointer", color: "#cbd5e1", lineHeight: 1 }}>
            ✕
          </button>
        </div>

        <div style={{ padding: "24px", overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
              1. Valore reale in questa colonna
            </div>
            {esempi.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button onClick={() => cambiaEsempio(Math.max(0, indiceEsempio - 1))} disabled={indiceEsempio === 0} style={{ padding: "2px 8px" }}>◀</button>
                <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>riga {indiceEsempio + 1} / {esempi.length}</span>
                <button onClick={() => cambiaEsempio(Math.min(esempi.length - 1, indiceEsempio + 1))} disabled={indiceEsempio === esempi.length - 1} style={{ padding: "2px 8px" }}>▶</button>
              </div>
            )}
          </div>
          <div style={{ padding: "10px 14px", borderRadius: "8px", border: "1px solid var(--cf-border-strong)", backgroundColor: "var(--cf-bg-subtle)", fontSize: "14px", color: "var(--cf-text-primary)", marginBottom: "20px", wordBreak: "break-word" }}>
            {valoreEsempio || <span className="testo-secondario">(vuoto per questa riga)</span>}
          </div>

          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
            2. Dove spezzare il testo
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "20px" }}>
            {SEPARATORI_RAPIDI.map((s) => (
              <button
                key={s.valore}
                onClick={() => scegliSeparatoreRapido(s.valore)}
                style={separatore === s.valore && !separatorePersonalizzato ? stileBtnAttivo : undefined}
              >
                {s.etichetta}
              </button>
            ))}
            <input
              type="text"
              placeholder="altro carattere..."
              value={separatorePersonalizzato}
              onChange={(e) => scegliSeparatorePersonalizzato(e.target.value)}
              style={{ width: "8rem" }}
            />
          </div>

          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
            3. Clicca l'ultima pillola della prima colonna
          </div>
          <p className="testo-secondario" style={{ marginTop: 0, marginBottom: "10px" }}>
            Tutto quello fino alla pillola cliccata (compresa) finisce nella prima colonna, il resto nella seconda.
          </p>

          <label className="riga-controlli" style={{ marginBottom: "10px", fontSize: "12px", color: "var(--cf-text-muted)" }}>
            <input type="checkbox" checked={terzaAttiva} onChange={(e) => cambiaTerzaAttiva(e.target.checked)} />
            C'è anche un pezzo in fondo da separare o scartare (es. la taglia "T34" che non c'è sempre): clicca prima
            la fine della 1ª colonna, poi la fine della 2ª
          </label>

          {parti.length === 0 ? (
            <p className="errore">Il valore di questa riga è vuoto: prova un'altra riga di esempio con la freccia sopra.</p>
          ) : (
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "18px" }}>
              {parti.map((parte, p) => {
                const inParte1 = indiceTaglio != null && p < indiceTaglio;
                const inParte3 = terzaAttiva && indiceTaglio != null && p >= fineEffettiva;
                return (
                  <button
                    key={p}
                    onClick={() => cliccaPillola(p)}
                    title={inParte1 ? "1ª colonna" : inParte3 ? "3ª colonna" : indiceTaglio != null ? "2ª colonna" : ""}
                    style={{
                      padding: "8px 14px", borderRadius: "10px",
                      border: inParte1 ? "2px solid #1e3a8a" : inParte3 ? "2px dashed var(--cf-border-strong)" : indiceTaglio != null ? "2px solid #f59e0b" : "1px solid var(--cf-border-strong)",
                      backgroundColor: inParte1 ? "#1e3a8a" : inParte3 ? "var(--cf-bg-subtle)" : indiceTaglio != null ? "rgba(245,158,11,0.15)" : "var(--cf-bg-subtle)",
                      color: inParte1 ? "#fff" : "var(--cf-text-primary)",
                      fontWeight: 600,
                    }}
                  >
                    {parte}
                  </button>
                );
              })}
            </div>
          )}

          {indiceTaglio != null && (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "18px" }}>
              <div style={{ padding: "10px 14px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid #1e3a8a" }}>
                <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>1ª colonna: </span>
                <span style={{ fontSize: "14px", fontWeight: 700 }}>"{parte1Anteprima}"</span>
              </div>
              <div style={{ padding: "10px 14px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px solid #f59e0b" }}>
                <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>2ª colonna: </span>
                <span style={{ fontSize: "14px", fontWeight: 700 }}>"{parte2Anteprima || "(vuota)"}"</span>
              </div>
              {parte3Anteprima != null && (
                <div style={{ padding: "10px 14px", borderRadius: "8px", backgroundColor: "var(--cf-bg-subtle)", border: "1px dashed var(--cf-border-strong)" }}>
                  <span style={{ fontSize: "12px", color: "var(--cf-text-muted)" }}>3ª colonna{nomeParte3.trim() ? "" : " (senza nome: scartata)"}: </span>
                  <span style={{ fontSize: "14px", fontWeight: 700 }}>"{parte3Anteprima || "(vuota)"}"</span>
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--cf-text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px", fontFamily: "'Roboto Mono', Consolas, monospace" }}>
            4. Nomi delle due colonne
          </div>
          <div className="riga-controlli">
            <input type="text" value={nomeParte1} onChange={(e) => setNomeParte1(e.target.value)} placeholder="es. UM" />
            <input type="text" value={nomeParte2} onChange={(e) => setNomeParte2(e.target.value)} placeholder="es. Supplier" />
            {terzaAttiva && (
              <input type="text" value={nomeParte3} onChange={(e) => setNomeParte3(e.target.value)} placeholder="3ª (vuoto = scarta)" />
            )}
          </div>
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--cf-border)", display: "flex", justifyContent: "flex-end", gap: "10px", flexShrink: 0 }}>
          <button onClick={onAnnulla}>Annulla</button>
          <button className="btn-primario" onClick={confermaScelta} disabled={!puoConfermare}>
            Dividi
          </button>
        </div>
      </div>
    </div>
  );
}
